import type { LandingState } from './interfaces'
import type { AvailableSlot } from '@brighterly/lib-core-types/landing/interfaces'
import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import type { SelectMode } from '@brighterly/lib-core-types/ui/selects/types'

import { useAvailableSlotsApi } from '@composables/api/landings/available-slots.hook'

const setAvailableSlots = (state: LandingState, payload: LandingState['availableSlots']): void => {
  state.availableSlots = payload
}

const clearValidation = (state: LandingState): void => {
  state.dateMode = 'normal'
  state.timeMode = 'normal'
}

const setKidId = (state: LandingState, payload: LandingState['kidId']): void => {
  state.kidId = payload
  clearValidation(state)
}

const resetForm = (state: LandingState): void => {
  state.selectedDate = null
  state.selectedTime = null
  state.formValid = false
  clearValidation(state)
}

export const actions = {
  setLoading (this: LandingState, payload: boolean): void {
    this.loading = payload
  },
  setAvailableSlots (this: LandingState, payload: AvailableSlot[]): void {
    return setAvailableSlots(this, payload)
  },

  async loadAvailableSlots (this: LandingState, payload: { kidId: string, month: string, extended?: boolean }): Promise<void> {
    try {
      const response = await useAvailableSlotsApi({
        kidId: payload.kidId,
        month: payload.month,
        extended: payload.extended,
      })

      if (response?.data?.data?.slots) {
        const { slots, is_long_window_auto } = response.data.data
        this.allSlots = slots
        this.isExtendedSlots = false
        this.longWindowAuto = is_long_window_auto
        setAvailableSlots(this, is_long_window_auto ? slots : slots.filter(slot => !slot.is_extended))
      }
    } catch (error) {
      console.error(error)
    }
  },

  setExtendedSlots (this: LandingState, payload: boolean): void {
    this.isExtendedSlots = payload
    setAvailableSlots(this, payload ? this.allSlots : this.allSlots.filter(slot => !slot.is_extended))
  },

  setSelectedDate (this: LandingState, payload: LandingState['selectedDate']): void {
    this.selectedDate = payload
    this.selectedTime = null
    if (payload) {
      this.dateMode = 'normal'
    }
  },

  setSelectedTime (this: LandingState, payload: LandingState['selectedTime']): void {
    this.selectedTime = payload
    if (payload) {
      this.timeMode = 'normal'
    }
  },

  setKidId (this: LandingState, payload: LandingState['kidId']): void {
    this.kidId = payload
    clearValidation(this)
  },

  setDisciplineCode (this: LandingState, payload: Discipline): void {
    this.disciplineCode = payload
    clearValidation(this)
  },

  setDateMode (this: LandingState, payload: SelectMode): void {
    this.dateMode = payload
  },

  setTimeMode (this: LandingState, payload: SelectMode): void {
    this.timeMode = payload
  },

  updateFormValid (this: LandingState): void {
    this.formValid = !!(this.selectedDate && this.selectedTime)
  },

  validateForm (this: LandingState): boolean {
    let isValid = true

    // Validate date
    if (!this.selectedDate) {
      this.dateMode = 'error'
      isValid = false
    } else {
      this.dateMode = 'normal'
    }

    if (this.selectedDate && !this.selectedTime) {
      this.timeMode = 'error'
      isValid = false
    } else if (this.selectedDate && this.selectedTime) {
      this.timeMode = 'normal'
    }
    return isValid
  },

  clearValidation (this: LandingState): void {
    this.dateMode = 'normal'
    this.timeMode = 'normal'
  },

  resetForm (this: LandingState): void {
    this.selectedDate = null
    this.selectedTime = null
    this.formValid = false
    clearValidation(this)
  },

  handleKidChange (this: LandingState, kidId: string | null): void {
    setKidId(this, kidId)
    resetForm(this)
    clearValidation(this)
    this.allSlots = []
    this.isExtendedSlots = false
    this.longWindowAuto = false
  },
  // Modal for demo booking success
  setModalBookDemoSuccess (this: LandingState, payload: LandingState['modalBookDemoSuccess']): void {
    this.modalBookDemoSuccess = payload
  },
}
